/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 */

import React, { useEffect, useState } from "react";
import Avatar from "react-avatar";
import { useParams } from "react-router-dom";
import { viewOrdersById } from "../../orders/core/_requests";
import { useAuth } from "../../../auth";
import { listChefWelletDetail } from "../core/_requests";
import { useFormattedUtils } from "../../../component/dateUtils";

const WalletDetails: React.FC = () => {
  const { id } = useParams();
  const { formatDateUS, formatTimeUS } = useFormattedUtils();

  const [wallet, setWallet] = useState<any>({});
  const [order, setOrder] = useState<any>({});

  const { currentUser } = useAuth();
  const chefId = currentUser?.uuid;

  const grossGrandTotal = wallet?.credit || 0;

  /* ================= Wallet API ================= */
  useEffect(() => {
    if (!chefId || !id) return;

    listChefWelletDetail(chefId, { uuid: id }).then((res) => {
      setWallet(res?.data?.[0] || {});
    });
  }, [chefId, id]);

  /* ================= Order API ================= */
  useEffect(() => {
    if (!wallet?.orderId) return;

    viewOrdersById({
      orderId: wallet.orderId, // supports #123 and 123
    }).then((res) => {
      setOrder(res?.data || {});
    });
  }, [wallet.orderId]);

  /* ================= Addons ================= */
  const addonsAmount =
    order?.orderdetails?.reduce((sum: number, item: any) => {
      const addonTotal =
        item?.orderdetailaddons?.reduce(
          (aSum: number, a: any) =>
            aSum + (a.quantity || 0) * (a.amount || 0),
          0
        ) || 0;
      return sum + addonTotal;
    }, 0) || 0;

  return (
    <section className="container-fluid">
      <div className="row">
        <section className="content-section">
          <section className="row main-content">
            <div className="col-12 mt-md-2 mt-0">
              <div className="tab-content">
                <div className="tab-pane fade show active">
                  <button
                    className="btn btn-primary d-flex align-items-center mb-2 px-2 py-1"
                    onClick={() => window.history.back()}
                  >
                    <i className="material-symbols-outlined me-1">reply</i>
                    Go Back
                  </button>

                  {/* ================= Wallet Details ================= */}
                  <div className="order-snippet mb-3">
                    <div className="os-head">
                      <h5 className="mb-0 medium primary-color">
                        Wallet Details
                      </h5>
                    </div>

                    <div className="row os-body mt-3">
                      <div className="col-lg-6 col-12">
                        <div className="bg-blk">
                          <div className="row mb-1">
                            <p className="col-7 mid-gray mb-0">
                              <small>Wallet Type</small>
                            </p>
                            <h6 className="col-5 mid-gray medium mb-0 amount">
                              {wallet.walletType}
                              {wallet.orderId && ` (${wallet.orderId})`}
                            </h6>
                          </div>

                          <div className="row mb-1">
                            <p className="col-7 mid-gray mb-0">
                              <small>Date</small>
                            </p>
                            <h6 className="col-5 mid-gray medium mb-0 amount">
                              {formatDateUS(wallet?.date)}
                            </h6>
                          </div>

                          {wallet?.credit > 0 && (
                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">
                                <small>Credit</small>
                              </p>
                              <h6 className="col-5 mid-gray medium mb-0 amount">
                                ${wallet.credit.toFixed(2)}
                              </h6>
                            </div>
                          )}

                          {wallet?.debit > 0 && (
                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">
                                <small>Debit</small>
                              </p>
                              <h6 className="col-5 mid-gray medium mb-0 amount">
                                ${wallet.debit.toFixed(2)}
                              </h6>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ================= Order Details ================= */}
                  {wallet?.orderId && order?.orderId && (
                    <div className="order-snippet mb-3">
                      <div className="os-head">
                        <h5 className="mb-0 medium primary-color">
                          Order - {order.orderId}
                        </h5>
                        <p className="mb-0 mid-gray">
                          <small>
                            {formatDateUS(order.orderDate)} -{" "}
                            {formatTimeUS(order.orderDate)}
                          </small>
                        </p>
                      </div>

                      <div className="row os-body mt-3">
                        {/* LEFT */}
                        <div className="col-lg-6 col-12">
                          <h6 className="mb-2">Order Details</h6>
                          <div className="bg-blk">
                            {order?.orderdetails?.map((item: any, i: number) => (
                              <div className="row mb-3" key={i}>
                                <div className="col-8">
                                  <div className="dish">
                                    <div className="thumbnail me-2">
                                      <img
                                        src={item.dish?.dishImage}
                                        alt={item.dish?.name}
                                      />
                                    </div>
                                    <p className="mb-0">
                                      <span className="fw-500">
                                        {item.quantity}
                                      </span>{" "}
                                      x {item.dish?.name}
                                    </p>
                                  </div>
                                </div>
                                <div className="col-4 text-end">
                                  <h6 className="mb-0">
                                    $
                                    {(item.quantity * item.amount).toFixed(2)}
                                  </h6>
                                </div>
                              </div>
                            ))}

                            <div className="row mt-3">
                              <p className="mb-1">
                                <small className="fw-500">
                                  Cooking Instructions
                                </small>
                              </p>
                              <p className="mb-0 tiny mid-gray">
                                {order?.cookingInstruction || "-"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* RIGHT */}
                        <div className="col-lg-6 col-12">
                          <h6 className="mb-2">Bill Details</h6>
                          <div className="bg-blk">
                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">
                                Order Total
                              </p>
                              <h6 className="col-5 mb-0 text-end">
                                ${order.amount?.toFixed(2)}
                              </h6>
                            </div>

                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">Tax</p>
                              <h6 className="col-5 mb-0 text-end">
                                ${order.tax?.toFixed(2)}
                              </h6>
                            </div>

                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">
                                Platform Fee (-)
                              </p>
                              <h6 className="col-5 mb-0 text-danger text-end">
                                ${order.platformFee?.toFixed(2)}
                              </h6>
                            </div>

                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">
                                Discount (-)
                              </p>
                              <h6 className="col-5 mb-0 text-danger text-end">
                                ${order.couponAmount?.toFixed(2)}
                              </h6>
                            </div>

                            <div className="row mb-1">
                              <p className="col-7 mid-gray mb-0">Tips</p>
                              <h6 className="col-5 mb-0 text-end">
                                ${order.chefTips?.toFixed(2)}
                              </h6>
                            </div>

                            <hr />

                            <div className="row">
                              <p className="col-7 fw-500 mb-0">
                                Gross Grand Total
                              </p>
                              <h6 className="col-5 fw-500 mb-0 text-end">
                                ${grossGrandTotal.toFixed(2)}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </section>
      </div>
    </section>
  );
};

export default WalletDetails;
